(() => {
  if (customElements.get('mgp-accessories')) return;
  class MgpAccessoryProduct extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      const data = this.querySelector('[data-accessory-data]');
      if (!data) return;
      this.initialized = true;
      this.data = JSON.parse(data.textContent);
      this.form = this.querySelector('.mgp-accessory-form');
      this.selects = [...this.querySelectorAll('[data-accessory-option]')];
      this.swatches = [...this.querySelectorAll('[data-accessory-swatch]')];
      this.swatches.forEach(swatch => swatch.addEventListener('click', () => {
        if (this.submitting || swatch.disabled) return;
        this.selects[Number(swatch.dataset.accessorySwatch)].value = swatch.dataset.value;
        this.error.hidden = true;
        this.updateSelection();
      }));
      this.button = this.querySelector('.mgp-accessory-submit');
      this.status = this.querySelector('[data-accessory-status]');
      this.error = this.querySelector('[data-accessory-error]');
      this.selects.forEach(select => {
        [...select.options].forEach(option => { option.dataset.label = option.textContent; });
        select.addEventListener('change', () => { this.error.hidden = true; this.updateSelection(); });
      });
      this.button.addEventListener('click', event => this.add(event));
      this.updateSelection();
      this.form.hidden = false;
    }
    updateSelection() {
      const variants = this.data.variants;
      this.selects.forEach((select, index) => {
        [...select.options].forEach(option => {
          if (!option.value) return;
          const matches = variants.filter(variant => variant.options[index] === option.value && this.selects.slice(0, index).every((prior, i) => !prior.value || variant.options[i] === prior.value));
          const available = matches.some(variant => variant.available);
          option.disabled = matches.length === 0;
          option.dataset.available = String(available);
          option.textContent = option.dataset.label + (available ? '' : ` — ${this.data.messages.unavailable}`);
        });
        if (select.value && select.selectedOptions[0].disabled && select.options.length > 1) select.value = '';
      });
      this.swatches.forEach(swatch => {
        const select = this.selects[Number(swatch.dataset.accessorySwatch)];
        const option = [...select.options].find(option => option.value === swatch.dataset.value);
        swatch.disabled = this.submitting || !option || option.disabled;
        swatch.classList.toggle('is-sold-out', option?.dataset.available === 'false');
        swatch.setAttribute('aria-pressed', String(select.value === swatch.dataset.value));
        swatch.title = swatch.dataset.value + (option?.dataset.available === 'false' ? ' — ' + this.data.messages.unavailable : '');
      });
      this.querySelectorAll('[data-accessory-option-value]').forEach(label => {
        label.textContent = this.selects[Number(label.dataset.accessoryOptionValue)].value;
      });
      const complete = this.selects.every(select => select.value);
      this.variant = complete ? variants.find(variant => this.selects.every((select, index) => variant.options[index] === select.value)) : null;
      const preview = this.variant || variants.find(variant => this.selects.every((select, index) => !select.value || variant.options[index] === select.value));
      const ready = !!this.variant?.available;
      this.button.disabled = !ready || this.submitting;
      this.button.textContent = this.data.messages.add;
      this.status.textContent = !variants.some(variant => variant.available) ? this.data.messages.unavailable : (!complete ? this.data.messages.select : (ready ? this.data.messages.stock : this.data.messages.unavailable));
      this.status.classList.toggle('is-available', ready);
      if (preview) {
        this.querySelector('[data-accessory-price]').textContent = preview.price;
        const compare = this.querySelector('[data-accessory-compare]');
        compare.textContent = preview.compare;
        compare.hidden = !preview.compare;
        const image = this.querySelector('.mgp-accessory-image img');
        if (image && preview.image) {
          image.removeAttribute('srcset');
          image.src = preview.image;
          image.alt = `${this.data.title} — ${preview.options.join(' / ')}`;
        }
      }
      const url = new URL(this.data.url, window.location.origin);
      if (preview) url.searchParams.set('variant', preview.id);
      this.querySelectorAll('[data-accessory-link]').forEach(link => { link.href = url.href; });
    }
    async add(event) {
      event.preventDefault();
      if (this.submitting || !this.variant?.available || !this.selects.every(select => select.reportValidity())) return;
      this.submitting = true;
      const variant = this.variant;
      this.button.disabled = true;
      this.button.textContent = this.data.messages.adding;
      this.selects.forEach(select => { select.disabled = true; });
      this.swatches.forEach(swatch => { swatch.disabled = true; });
      this.error.hidden = true;
      document.dispatchEvent(new CustomEvent('theme:loading:start'));
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST', credentials: 'same-origin',
          headers: {'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
          body: JSON.stringify({items: [{id: variant.id, quantity: 1}]})
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || this.data.messages.error);
        if (this.closest('.mgp-cart-accessories')) {
          window.location.reload();
          return;
        }
        this.dispatchEvent(new CustomEvent('product:added', {bubbles: true, detail: {button: this.button, variant, quantity: 1, productAdded: true}}));
        if (window.theme?.cartType === 'page') window.location.assign(window.routes?.cartUrl || `${window.Shopify?.routes?.root || '/'}cart`);
      } catch (error) {
        this.error.textContent = error instanceof TypeError ? this.data.messages.error : error.message;
        this.error.hidden = false;
      } finally {
        this.submitting = false;
        this.selects.forEach(select => { select.disabled = false; });
        this.updateSelection();
        document.dispatchEvent(new CustomEvent('theme:loading:end'));
      }
    }
  }
  if (!customElements.get('mgp-accessory-product')) customElements.define('mgp-accessory-product', MgpAccessoryProduct);

  let cartIds = null;
  let refreshPromise;
  const updateCart = () => {
    if (refreshPromise) return refreshPromise;
    const root = window.Shopify?.routes?.root || '/';
    refreshPromise = fetch(`${root}cart.js`, {credentials: 'same-origin', headers: {'Accept': 'application/json'}})
      .then(r => { if (!r.ok) throw new Error('Cart unavailable'); return r.json(); })
      .then(cart => { cartIds = new Set(cart.items.map(i => String(i.product_id))); document.querySelectorAll('mgp-accessories').forEach(el => el.refresh()); })
      .catch(() => {})
      .finally(() => { refreshPromise = null; });
    return refreshPromise;
  };
  class MgpAccessories extends HTMLElement {
    connectedCallback() {
      this.variantId = this.dataset.sourceVariant;
      this.onVariant = event => {
        const variant = event.detail?.variant;
        if (!variant || !this.dataset.productId) return;
        if (variant.product_id && String(variant.product_id) !== this.dataset.productId) return;
        const sourceForm = event.target?.closest?.('form');
        if (sourceForm && !sourceForm.id.includes(this.dataset.productId)) return;
        this.variantId = String(variant.id);
        this.refresh();
      };
      document.addEventListener('variant:change', this.onVariant);
      this.refresh();
      updateCart();
    }
    disconnectedCallback() { document.removeEventListener('variant:change', this.onVariant); }
    refresh() {
      let visible = 0;
      this.querySelectorAll('.mgp-accessory-row').forEach(row => {
        const allowed = row.dataset.sourceVariants;
        const variantFits = !this.dataset.productId || !allowed || allowed.split(',').includes(String(this.variantId));
        row.hidden = !variantFits || !!cartIds?.has(row.dataset.accessoryId);
        if (!row.hidden) visible++;
      });
      const more = this.querySelector('.mgp-accessories__more');
      if (more) {
        const count = [...more.querySelectorAll('.mgp-accessory-row')].filter(row => !row.hidden).length;
        more.hidden = count === 0;
        const label = more.querySelector('[data-accessories-more-count]');
        if (label) label.textContent = String(count);
      }
      this.hidden = visible === 0;
    }
  }
  customElements.define('mgp-accessories', MgpAccessories);
  document.addEventListener('cart:updated', updateCart);
  document.addEventListener('cart:refresh', updateCart);
  document.addEventListener('product:added', updateCart);
  document.addEventListener('cart:change', updateCart);
  document.addEventListener('cart:rerendered', updateCart, true);
})();


